import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function PartnerSettings() {
  const [rest, setRest] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/partner/me");
    setRest(data.restaurant);
    if (data.restaurant) {
      setForm({
        name: data.restaurant.name,
        blurb: data.restaurant.blurb,
        contact_phone: data.restaurant.contact_phone,
        contact_email: data.restaurant.contact_email,
        gstin: data.restaurant.gstin,
        dining_model: data.restaurant.dining_model,
        serves_alcohol: !!data.restaurant.serves_alcohol,
        partner_status: data.restaurant.partner_status || "active",
        has_banquet: !!data.restaurant.has_banquet,
        banquet_min_pax: data.restaurant.banquet_min_pax || 100,
        banquet_max_pax: data.restaurant.banquet_max_pax || 500,
        default_allergens: data.restaurant.default_allergens || [],
        max_concurrent_pax_dining: data.restaurant.max_concurrent_pax_dining ?? 60,
        max_concurrent_large_groups: data.restaurant.max_concurrent_large_groups ?? 2,
        large_group_pax_threshold: data.restaurant.large_group_pax_threshold ?? 15,
        banquet_max_concurrent_events: data.restaurant.banquet_max_concurrent_events ?? 1,
      });
    }
  };
  useEffect(() => { load(); }, []);

  const toggleDefaultAllergen = (a) => {
    setForm((f) => ({
      ...f,
      default_allergens: (f.default_allergens || []).includes(a)
        ? f.default_allergens.filter((x) => x !== a)
        : [...(f.default_allergens || []), a],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/partner/settings", form);
      toast.success("Settings saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (!rest) return <div className="text-light-ink animate-pulse">Loading...</div>;

  return (
    <div className="space-y-5 animate-fade-up max-w-2xl">
      <header>
        <p className="text-amber text-xs uppercase tracking-[0.18em] font-medium mb-2">Settings</p>
        <h1 className="font-serif text-4xl">{rest.name}</h1>
        <p className="text-light-ink text-sm mt-2">Profile, operating model and feature flags. Changes apply instantly.</p>
      </header>

      <div className="tablo-card p-6 space-y-4">
        <h2 className="font-serif text-2xl">Profile</h2>
        <div>
          <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Name</label>
          <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border-tablo-border" data-testid="settings-name"/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Blurb</label>
          <Textarea value={form.blurb || ""} onChange={(e) => setForm({ ...form, blurb: e.target.value })} className="bg-white border-tablo-border min-h-[60px]"/>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Contact phone</label>
            <Input value={form.contact_phone || ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="bg-white border-tablo-border"/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Contact email</label>
            <Input value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="bg-white border-tablo-border"/>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">GSTIN</label>
          <Input value={form.gstin || ""} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className="bg-white border-tablo-border"/>
        </div>
      </div>

      <div className="tablo-card p-6 space-y-4">
        <h2 className="font-serif text-2xl">Operating model</h2>
        <div>
          <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">How groups book</label>
          <Select value={form.dining_model} onValueChange={(v) => setForm({ ...form, dining_model: v })}>
            <SelectTrigger className="bg-white border-tablo-border" data-testid="settings-dining-model"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="package_only">Package only — chef curates by pax</SelectItem>
              <SelectItem value="a_la_carte">À-la-carte — Tablo Curate or guest picks</SelectItem>
              <SelectItem value="hybrid">Hybrid — both</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between bg-cream rounded-xl p-4">
          <div>
            <p className="font-medium">We serve alcohol</p>
            <p className="text-xs text-light-ink">When off, beer/wine/cocktails are hidden from booker menus.</p>
          </div>
          <Switch checked={!!form.serves_alcohol} onCheckedChange={(v) => setForm({ ...form, serves_alcohol: v })} data-testid="settings-alcohol"/>
        </div>
        <div className="flex items-center justify-between bg-cream rounded-xl p-4">
          <div>
            <p className="font-medium">Accepting new bookings</p>
            <p className="text-xs text-light-ink">Turn off during heavy rush — existing bookings stay, but new bookers won't see you in Discovery.</p>
          </div>
          <Switch
            checked={form.partner_status === "active"}
            onCheckedChange={(v) => setForm({ ...form, partner_status: v ? "active" : "paused" })}
            data-testid="settings-partner-status"
          />
        </div>

        {/* Banquet hall toggle */}
        <div className="rounded-xl border border-tablo-border bg-white/60 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">We host banquets (100+ guests)</p>
              <p className="text-xs text-light-ink">Show up in Tablo for large events (weddings, conferences, milestones). Banquet packages are separate from regular dining.</p>
            </div>
            <Switch
              checked={!!form.has_banquet}
              onCheckedChange={(v) => setForm({ ...form, has_banquet: v })}
              data-testid="settings-has-banquet"
            />
          </div>
          {form.has_banquet && (
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-tablo-border">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-light-ink mb-1 block">Min banquet pax</label>
                <Input type="number" value={form.banquet_min_pax || 100} onChange={(e) => setForm({ ...form, banquet_min_pax: parseInt(e.target.value) || 100 })} className="bg-white border-tablo-border" data-testid="settings-banquet-min"/>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-light-ink mb-1 block">Max banquet pax</label>
                <Input type="number" value={form.banquet_max_pax || 500} onChange={(e) => setForm({ ...form, banquet_max_pax: parseInt(e.target.value) || 500 })} className="bg-white border-tablo-border" data-testid="settings-banquet-max"/>
              </div>
            </div>
          )}
        </div>

        {/* Default allergens used in the kitchen — shown to bookers */}
        <div className="rounded-xl border border-tablo-border bg-white/60 p-4 space-y-2">
          <p className="font-medium">Allergens commonly used in our kitchen</p>
          <p className="text-xs text-light-ink">Bookers see this on the venue page so allergic guests can plan around it. Per-dish allergens come from the menu items.</p>
          <div className="flex flex-wrap gap-1.5 pt-1" data-testid="settings-default-allergens">
            {["nuts", "peanut", "dairy", "gluten", "egg", "shellfish", "soy", "sesame", "fish", "mustard"].map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => toggleDefaultAllergen(a)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${(form.default_allergens || []).includes(a) ? "bg-rose-600 text-white border-rose-600" : "bg-white text-ink border-tablo-border hover:border-rose-400"}`}
                data-testid={`settings-default-allergen-${a}`}
              >
                {a}
              </button>
            ))}
          </div>
        </div>

        {/* Concurrent capacity — how many bookings the venue can hold at once */}
        <div className="rounded-xl border border-tablo-border bg-white/60 p-4 space-y-3" data-testid="settings-capacity">
          <p className="font-medium">Concurrent booking capacity</p>
          <p className="text-xs text-light-ink">
            Tablo blocks new bookings the moment your slot is full. The cap applies to overlapping bookings (±3 hrs).
          </p>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-light-ink mb-1 block">Max pax / slot (dining)</label>
              <Input
                type="number"
                value={form.max_concurrent_pax_dining ?? 60}
                onChange={(e) => setForm({ ...form, max_concurrent_pax_dining: parseInt(e.target.value) || 0 })}
                className="bg-white border-tablo-border"
                data-testid="settings-max-pax"
              />
              <p className="text-[10px] text-light-ink/70 mt-1">Total guests across all groups in a 3-hr window.</p>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-light-ink mb-1 block">Max large groups / slot</label>
              <Input
                type="number"
                value={form.max_concurrent_large_groups ?? 2}
                onChange={(e) => setForm({ ...form, max_concurrent_large_groups: parseInt(e.target.value) || 0 })}
                className="bg-white border-tablo-border"
                data-testid="settings-max-large"
              />
              <p className="text-[10px] text-light-ink/70 mt-1">e.g. 2 large tables side-by-side.</p>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-light-ink mb-1 block">"Large group" threshold</label>
              <Input
                type="number"
                value={form.large_group_pax_threshold ?? 15}
                onChange={(e) => setForm({ ...form, large_group_pax_threshold: parseInt(e.target.value) || 0 })}
                className="bg-white border-tablo-border"
                data-testid="settings-large-threshold"
              />
              <p className="text-[10px] text-light-ink/70 mt-1">Group sizes ≥ this count as "large".</p>
            </div>
          </div>
          {form.has_banquet && (
            <div className="pt-3 border-t border-tablo-border">
              <label className="text-[11px] uppercase tracking-wider text-light-ink mb-1 block">Banquet events / day</label>
              <Input
                type="number"
                value={form.banquet_max_concurrent_events ?? 1}
                onChange={(e) => setForm({ ...form, banquet_max_concurrent_events: parseInt(e.target.value) || 0 })}
                className="bg-white border-tablo-border max-w-xs"
                data-testid="settings-max-banquet"
              />
              <p className="text-[10px] text-light-ink/70 mt-1">Most venues run 1 banquet at a time.</p>
            </div>
          )}
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="w-full tablo-btn-primary" data-testid="settings-save">{saving ? "Saving..." : "Save changes"}</Button>
    </div>
  );
}
